import React from 'react';
import { ArrowRight, Check } from 'lucide-react';

export interface StagingStepperProps {
  view: string;
  hasPhoto: boolean;
}

const labels = ['Foto', 'Ruimte', 'Kies', 'Klaar'];
const styles = {
  current: 'border-orange-700 bg-orange-50 text-orange-900 font-bold ring-1 ring-orange-700',
  completed: 'border-emerald-700 bg-emerald-50 text-emerald-900 font-semibold',
  future: 'border-stone-300 bg-white text-stone-600 font-medium',
};

/** Display-only progress: the existing view/photo state remains the source of truth. */
export const StagingStepper: React.FC<StagingStepperProps> = ({ view, hasPhoto }) => {
  const current = view === 'studio' ? 3 : view === 'variants' ? 2 : view === 'dashboard' ? (hasPhoto ? 1 : 0) : -1;
  // Batch combines multiple independent jobs: retain the header steps but do not
  // pretend that one single-photo step is active (matching the previous header).
  if (current < 0 && view !== 'batch') return null;

  return (
    <nav aria-label="Voortgang" className="hidden md:flex">
      <ol className="flex items-center gap-1 lg:gap-2 text-[11px] tracking-[0.04em] uppercase">
        {labels.map((label, index) => {
          const state = index < current ? 'completed' : index === current ? 'current' : 'future';
          return (
            <React.Fragment key={label}>
              {index > 0 && <li aria-hidden="true" role="presentation"><ArrowRight size={12} className="text-stone-400" /></li>}
              <li
                data-state={state}
                aria-current={state === 'current' ? 'step' : undefined}
                className={`flex items-center gap-1.5 rounded-full border px-1 lg:px-2.5 py-1 ${styles[state]}`}
              >
                <span aria-hidden="true" className="flex h-4 w-4 items-center justify-center">
                  {state === 'completed' ? <Check size={14} strokeWidth={3} /> : index + 1}
                </span>
                <span className="sr-only lg:not-sr-only">{label}</span>
                <span className="sr-only"> — {state === 'current' ? 'Huidige stap' : state === 'completed' ? 'Voltooid' : 'Nog te doen'}</span>
              </li>
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
};
